import { CardUser, CardUserActionType, requestCardUserLog, useRenewCardUser } from '@/models/card';
import { DateFormat, DateTimeFullDisplayFormat, formatPeriod } from '@/models/common';
import { Form, Select, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useEffect } from 'react';
import Modal from '../Modal';
import ModalSelect from '../ModalSelect';
import { NumericKeyboardInput } from '../NumericKeyboard';
import PosButton from '../PosButton';
dayjs.extend(duration);

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
};

const unitOptions = [
  {
    label: '天',
    value: 'D',
  },
  // {
  //   label: '周',
  //   value: 'W',
  // },
  {
    label: '月',
    value: 'M',
  },
  {
    label: '年',
    value: 'Y',
  },
];
const unitSelectOptions = unitOptions.map(v => (
  <Select.Option key={v.value} value={v.value}>
    {v.label}
  </Select.Option>
));

export const CardRenewForm = ({
  cardUser,
  onRenew,
}: {
  cardUser: CardUser;
  onRenew: (updatedCardUser: CardUser) => void;
}) => {
  const { send: renewCardUser, loading: renewCardUserLoading } = useRenewCardUser();
  const { send: sendGetCardUserLog, payload: cardUserLogPayload, loading: cardUserLogLoading } = requestCardUserLog();
  const [form] = Form.useForm();

  useEffect(() => {
    sendGetCardUserLog({
      params: {
        cardUserId: cardUser.id,
        page: 1,
        limit: 10,
        actionType: CardUserActionType.RENEW,
      },
    });
  }, [cardUser.id]);

  // ISO-8601 Duration
  return (
    <Form
      {...formItemLayout}
      form={form}
      onFinish={async v => {
        Modal.confirm({
          title: '二次确认',
          content: `确定要续期${formatPeriod(`P${v.value}${v.unit}`)}吗？`,
          onOk: async () => {
            const updatedCardUser = await renewCardUser({
              pathVars: {
                sn: cardUser.sn,
              },
              data: `P${v.value}${v.unit}`,
            });
            onRenew(updatedCardUser);
          },
        });
      }}
      initialValues={{
        unit: 'M',
      }}>
      <Form.Item label="续期时长">
        <Space.Compact>
          <Form.Item name="value" noStyle rules={[{ required: true, message: '请填写续期时长' }]}>
            <NumericKeyboardInput
              inputProps={{
                min: 1,
              }}
              keyboardProps={{
                parseValueToNumber: true,
                showAllClean: true,
                trigger: 'click',
              }}
            />
          </Form.Item>
          <Form.Item name="unit" noStyle>
            <ModalSelect>{unitSelectOptions}</ModalSelect>
          </Form.Item>
        </Space.Compact>
      </Form.Item>
      <Form.Item
        label="有效期至"
        shouldUpdate={(prev, next) => {
          return prev.unit !== next.unit || prev.value !== next.value;
        }}>
        {({ getFieldValue }) => {
          const durationObj = dayjs.duration(`P${getFieldValue('value')}${getFieldValue('unit')}`);
          return <div>{dayjs(cardUser.expiredTime).add(durationObj).format(DateFormat)}</div>;
        }}
      </Form.Item>
      <Form.Item shouldUpdate label=" " colon={false}>
        {() => (
          <PosButton
            loading={renewCardUserLoading}
            htmlType="submit"
            type="primary"
            disabled={!form.getFieldValue('value') || form.getFieldsError().some(({ errors }) => errors.length > 0)}>
            确定续期
          </PosButton>
        )}
      </Form.Item>
      <Form.Item label="续期记录">
        {cardUserLogLoading ? (
          <Spin />
        ) : cardUserLogPayload?.list?.length ? (
          <Space direction="vertical" size={10}>
            {cardUserLogPayload?.list.map(item => (
              <div key={item.id}>
                续期 {formatPeriod(item.changeValue)}, 时间: {dayjs(item.addTime).format(DateTimeFullDisplayFormat)}
              </div>
            ))}
          </Space>
        ) : (
          <>无</>
        )}
      </Form.Item>
    </Form>
  );
};
